import React, { useEffect, useState } from "react";
import styles from "./institution-details.module.css";
import { Popconfirm, Tabs, Typography } from "antd";
import { CheckCircleOutlined, LeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getInstitutionDetails,
  suspendInstitution,
} from "../../../../services/institution";
import PaymentHistory from "../payment-history/payment-history";
import StatusChip from "../../../../components/status-chip/status-chip";
import InstitutionAdministrators from "../institution-administrations/institution-administrators";
import CustomButton from "../../../../common/custom-button/custom-button";
import { formatDate } from "../../../../utils/format-date/format-date";
import { toast } from "react-toastify";
import RoutesConstant from "../../../../constants/routes";

const InstitutionDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const institutionId = params.get("uid");
  const [detailsData, setDetailsData] = useState(null);

  useEffect(() => {
    institutionId && getInstitutionData();
  }, []);

  const getInstitutionData = async () => {
    try {
      const response = await getInstitutionDetails(institutionId);
      if (response?.data) {
        setDetailsData(response?.data?.data);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const onSuspendAccount = async (isSuspend) => {
    try {
      const payload = {
        id: institutionId,
        suspend: isSuspend,
      };
      const response = await suspendInstitution(payload);
      if (response?.data) {
        toast.success(`${isSuspend ? "Suspended" : "Activated"} successfully`);
        getInstitutionData();
      }
    } catch (error) {
      console.log("error", error);
      toast.error("Error! please try again");
    }
  };

  const tabItems = [
    {
      key: "1",
      label: "School Administrators",
      children: <InstitutionAdministrators institutionId={institutionId} />,
    },
    {
      key: "2",
      label: "Payment History",
      children: <PaymentHistory institutionId={institutionId} />,
    },
  ];

  const handleEditDetails = (event) => {
    event.preventDefault();
    navigate(`${RoutesConstant.INSTITUTION_FORM}?uid=${detailsData?.id}`);
  };

  const leftFields = [
    { label: "School Type", value: detailsData?.school_type },
    { label: "Curriculum", value: detailsData?.curriculum },
    { label: "Total Students", value: detailsData?.totalStudents },
    { label: "Address", value: detailsData?.physical_address },
    { label: "Phone no", value: detailsData?.phone_number },
    {
      label: "Total Revenue",
      value: detailsData?.total_revenue ?? "0 Z$",
      icon: "/assets/images/amount.svg",
    },
    { 
      label: "Onboarded on", 
      value: detailsData?.created_at ? formatDate(detailsData?.created_at) : "-" 
    },
  ];

  const rightFields = [
    {
      label: "Established",
      value: detailsData?.established_year
        ? formatDate(detailsData?.established_year)
        : "-",
    },
    { label: "Ownership Type", value: detailsData?.ownership_type },
    { label: "Total Teachers", value: detailsData?.totalTeachers },
    { label: "Province", value: detailsData?.province },
    { label: "Email", value: detailsData?.email_address },
    { label: "Pending Amount", value: detailsData?.pending_amount ?? "0 Z$" },
  ];

  const renderField = (field, index) => (
    <div key={index} className={styles.row}>
      <Typography className={styles.infoLabelClass}>{field?.label}:</Typography>
      <div className={styles.valueWrapper}>
        <Typography className={styles.infoValueClass}>
          {field?.value || "-"}
        </Typography>
        {field?.icon && (
          <img
            src={field?.icon}
            alt=""
            className={styles.revenueIcon}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.leftSection} onClick={() => navigate(-1)}>
          <LeftOutlined />
          <Typography className={styles.navTitle}>
            {detailsData?.institution_name ?? "-"}
          </Typography>
        </div>
        <div className={styles.rightActions}>
          <CustomButton
            title="Send Mail"
            className={styles.buttonStyle}
            style={{
              backgroundColor: "transparent",
              border: "none",
              color: "#39B54A",
              fontWeight: 500,
              fontSize: "14px",
            }}
            icon={<img src="/assets/images/messageIcon.svg" alt="mail" />}
          />
          <CustomButton
            title="Edit Details"
            onClick={handleEditDetails}
            className={styles.buttonStyle}
            style={{
              backgroundColor: "transparent",
              border: "none",
              color: "#39B54A",
              fontWeight: 500,
              fontSize: "14px",
            }}
            icon={<img src="/assets/images/editDetails.svg" alt="mail" />}
          />
          <Popconfirm
            title={detailsData?.is_active ? "Suspend" : "Active"}
            description={`Are you sure you want to ${
              detailsData?.is_active ? "suspend" : "active"
            } this account?`}
            okText="Yes"
            cancelText="No"
            onConfirm={() => onSuspendAccount(detailsData?.is_active)}
          >
            <span>
              {detailsData?.is_active ? (
                <CustomButton
                  title="Suspend Account"
                  className={styles.buttonStyle}
                  style={{
                    backgroundColor: "#F349111A",
                    border: "none",
                    color: "#F31111",
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                  icon={<img src="/assets/images/suspendIcon.svg" alt="mail" />}
                />
              ) : (
                <CustomButton
                  title="Active Account"
                  style={{
                    backgroundColor: "#eefdf9",
                    border: "none",
                    color: "#3bb44b",
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                  icon={<CheckCircleOutlined />}
                />
              )}
            </span>
          </Popconfirm>
        </div>
      </div>
      <div className={styles.detailsContainer}>
        <div className={styles.imgContainer}>
          <img
            src={
              detailsData?.institution_logo ?? "assets/images/sample-school.jpg"
            }
            alt="sample"
          />
        </div>
        <div className={styles.contentContainer}>
          <div className={styles.schoolDataRow}>
            <div className={styles.institutionInfo}>
              <Typography className={styles.schoolTitle}>
                {detailsData?.institution_name ?? "-"}
              </Typography>
              <StatusChip
                status={detailsData?.is_active ? "active" : "inactive"}
              />
            </div>
            <div className={styles.schoolTimeZoneCurrency}>
              <Typography>
                Time Zone: {detailsData?.time_zone ?? "N/A"}
              </Typography>
              <Typography>
                Currency: {detailsData?.currencies ?? "N/A"}
              </Typography>
            </div>
          </div>

          <div className={styles.schoolsType}>
            <Typography>
              Registration/Accreditation Number:
            </Typography>
            <Typography className={styles.infoValueClass}>
              {detailsData?.registration_number || "-"}
            </Typography>
          </div>

          <div className={styles.detailsInfoContainer}>
            <div className={styles.detailsInfoColumn}>
              {leftFields?.map(renderField)}
            </div>
            <div className={styles.detailsInfoColumn}>
              {rightFields?.map(renderField)}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.tabContainer}>
        <Tabs defaultActiveKey="1" items={tabItems} />
      </div>
    </div>
  );
};

export default InstitutionDetails;
